import {
    Autocomplete,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    LinearProgress,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {useState} from "react";
import {User} from "../../data/interfaces";


interface AddAccountMembersDialogProps {
    open: boolean;
    onClose: () => void;
    onAddUsers: (users: string[]) => void;
    users: User[]
}

export default function AddUsersDialog({
                                           open,
                                           onClose,
                                           onAddUsers,
                                           users
                                       }: AddAccountMembersDialogProps) {
    const [autocompleteValues, setAutoCompleteValues] = useState<string[]>([]);
    const [alertDialogOpen, setAlertDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const isValidEmail = (email: string) =>
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
            email
        );

    const containAnyValidEmail = () => {
          return autocompleteValues.some(
            (email) => isValidEmail(email) && !userAlreadyExists(email)
          )
            ? true
            : false;
      };

    const handleSendInvite = () => {
        const uniqueValues = autocompleteValues.filter(
            (val, id, array) => array.indexOf(val) === id
        );
        const validEmails = uniqueValues.filter((email) => isValidEmail(email));
        setLoading(true);
        onAddUsers(validEmails);
    };

    const confirmSendInvite = () => {
        const containInvalidEmail = autocompleteValues.some(
            (email) => !isValidEmail(email)
        );
        if (containInvalidEmail) {
            setAlertDialogOpen(true);
        } else {
            handleSendInvite();
        }
    };

    const userAlreadyExists = (email: string) => {
        return users.some(u => u.email === email);
    }

    const validateEmail = (email: string) => {
        email = email.toLowerCase();
        if (!isValidEmail(email)) {
            return "Invalid email";
        } else if (userAlreadyExists(email)) {
            return "User already exists";
        } else {
            return undefined;
        }
    };

    const handleAutoCompleteValues = (inputValues: string[]) => {
        const previousValues = inputValues.slice(0, -1);
        const lastValue = inputValues.slice(-1)[0];
        const regex = /<([^\s<>@]+@[^\s<>@]+)>/g;

        if (inputValues.length > 0) {
            let parsedLastValue = Array.from(lastValue.matchAll(regex), (m) => m[1]);
            if (parsedLastValue === undefined || parsedLastValue.length === 0) {
                setAutoCompleteValues([...previousValues, lastValue]);
            } else {
                setAutoCompleteValues(previousValues.concat(parsedLastValue));
            }
        } else {
            setAutoCompleteValues([]);
        }
    };

    const renderTags = (values: string[], getTagProps: any) => (
        <div
            style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "5px",
                width: "100%",
            }}
        >
            {values.map((email: string, index: number) => {
                const error = validateEmail(email);
                const renderChip = (props?: any) => (
                    <Chip
                        {...props}
                        {...getTagProps({index})}
                        label={
                            <Typography style={{whiteSpace: "normal"}}>{email}</Typography>
                        }
                        size="small"
                        color={error ? "error" : "default"}
                        style={{height: "100%"}}
                    />
                );
                if (error) {
                    return (
                        <Tooltip title={error} key={index}>
                            {renderChip()}
                        </Tooltip>
                    );
                } else {
                    return renderChip({key: index});
                }
            })}
        </div>
    );

    return (
        <div>
            <Dialog open={open} onClose={onClose} fullWidth={true} maxWidth="md">
                {loading ? <LinearProgress></LinearProgress> : null}
                <DialogTitle>Invite users by email</DialogTitle>
                <DialogContent>
                    <Autocomplete
                        sx={{mt: 1}}
                        fullWidth
                        multiple
                        freeSolo
                        value={autocompleteValues}
                        onChange={(event: any, values: string[]) =>
                            handleAutoCompleteValues(values)
                        }
                        options={[]}
                        renderTags={renderTags}
                        renderInput={(params: any) => (
                            <TextField
                                {...params}
                                label="Type an email address and press enter to add it"
                                multiline
                                minRows={5}
                            />
                        )}
                    />
                    <Grid container spacing={2} sx={{mt: 2, pl: 2}}>
                        <Grid item xs={3}>
                            <Typography sx={{fontWeight: "bold"}}>Example for copy-pasting:</Typography>
                        </Grid>
                        <Grid item xs>
                            <Typography>
                                John Doe &lt;john.doe@email.com&gt;; Jane Doe
                                &lt;jane.doe@email.com&gt;
                            </Typography>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button
                        variant="outlined"
                        disabled={!containAnyValidEmail()}
                        onClick={confirmSendInvite}
                    >
                        Send invite
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={alertDialogOpen} onClose={() => setAlertDialogOpen(false)}>
                <DialogTitle>{"Confirm sending invite?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText>Invalid values will be skipped.</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAlertDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSendInvite} autoFocus>
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}
